import { UseMutationResult, useMutation, useQueryClient } from 'react-query';

import { revokeUser } from 'src/utils/urls';
import { useAuth } from 'src/hooks';

type ApiErrorItem = {
  Code: string;
  FieldName: string;
  Message: string;
};

type TResult<TData = unknown> = {
  Errors: ApiErrorItem[];
  ErrorId: string;
  IsSuccess: boolean;
  StatusCode: number;
  Data?: TData;
};

type TVariables = {
  token: string;
  tokenType: string;
  userId: string;
};

type TError = Error;

async function deleteUser({
  token,
  tokenType,
  userId,
}: TVariables): Promise<TResult> {
  const response = await fetch(revokeUser(userId), {
    method: 'DELETE',
    headers: {
      Authorization: `${tokenType} ${token}`,
      'Content-Type': 'application/json',
    },
  });

  let data: TResult | null = null;

  try {
    const text = await response.text();
    data = text ? JSON.parse(text) : null;
  } catch (e) {
    console.error('JSON parse failed:', e);
  }

  if (!data && response.ok) {
    return {
      Errors: [],
      ErrorId: '',
      IsSuccess: true,
      StatusCode: response.status,
    };
  }

  if (!data) {
    throw new Error('Invalid server response');
  }

  return data;
}

function useRevokeAccess(): UseMutationResult<
  TResult,
  TError,
  string,
  unknown
> {
  const { token, tokenType } = useAuth();
  const queryClient = useQueryClient();

  return useMutation(async (userId: string) => {
    const result = await deleteUser({
      token,
      tokenType,
      userId,
    });

    if (!result.IsSuccess) {
      const errorMessage =
        result.Errors?.map(err => err.Message).join(', ') ||
        'Failed to revoke user';

      throw new Error(errorMessage);
    }

    queryClient.invalidateQueries('get_users_list');

    return result;
  });
}

export { useRevokeAccess };
